import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, OnDestroy, AfterViewInit, ViewChild, ElementRef, NgZone } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { HighlightBox, HighlightVariant } from './document-highlight-layer/document-highlight-layer.component';
import { HotspotRegion } from './document-hotspot-layer/document-hotspot-layer.component';
import { decodeRowSpan, SheetRowSpan } from './sheet-range.util';
import * as XLSX from 'xlsx';
import { environment } from 'src/environments/environment';
import { LocalStorageService } from 'src/app/shared/localstorage.service';

type CellView = {
  text: string;
  raw: any;
  formula?: string | null;
  address: string;
  hidden?: boolean;
  rowspan?: number;
  colspan?: number;
};

type SheetView = {
  name: string;
  grid: CellView[][];
  rows: number;
  cols: number;
  visible: boolean;
  hidden?: boolean;
  /** Used-range origin of `grid`, absolute and 0-indexed. Non-zero on any sheet not starting at A1 (WB-7592). */
  startR: number;
  startC: number;
};


/** What the host passes to highlight on the rendered document (boxes + optional 1-indexed page). */
export interface DocumentHighlight {
  boxes: HighlightBox[];
  page?: number;
}

/** A hoverable line region on the document (bidirectional hover, FE-7). Re-exported for hosts. */
export type DocumentLineRegion = HotspotRegion;

/**
 * Spreadsheet highlight (WB-7592). Separate from `highlight` because the substrates differ: a box is
 * a rect measured over a canvas, this addresses DOM cells by A1 range.
 */
export interface DocumentCellHighlight {
  sheet?: string;
  /** Absolute A1 single-row spans, weakest tier first — later tiers draw over earlier ones. */
  ranges: Array<{ range: string; variant?: HighlightVariant }>;
}

/** A hoverable sheet row for reverse hover. `row` is ABSOLUTE and 1-indexed. */
export interface DocumentCellRegion {
  key: string;
  sheet?: string;
  row: number;
}

/** A measured rendered-page rect + the line regions on that page — one per page so doc→list hover
 *  works on every page of a multi-page PDF, not just the active one. */
export interface PdfPageHotspots {
  page: number;
  left: number;
  top: number;
  width: number;
  height: number;
  regions: DocumentLineRegion[];
}

@Component({
  selector: 'app-files-previewer',
  templateUrl: './files-previewer.component.html',
  styleUrls: ['./files-previewer.component.scss']
})
export class FilesPreviewerComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {

  @Input() data: any;
  @Input() isNotExport: boolean = false;
  /** Document Coordinates: optional highlight (normalized boxes + 1-indexed page) to overlay. */
  @Input() highlight: DocumentHighlight | null = null;
  /** Document Coordinates (FE-7): hoverable line regions for doc→list reverse hover. */
  @Input() lineRegions: DocumentLineRegion[] = [];
  /** Document Coordinates (WB-7592): optional A1 row highlight for the spreadsheet preview. */
  @Input() cellHighlight: DocumentCellHighlight | null = null;
  /** Document Coordinates (WB-7592): hoverable sheet rows for spreadsheet doc→list reverse hover. */
  @Input() cellRegions: DocumentCellRegion[] = [];
  /** Document Coordinates (FE-7): emits the hovered region's line key (or null on leave). */
  @Output() lineHover = new EventEmitter<string | null>();
  isExcelOrCsvFile: boolean = false;
  isPdfFile: boolean = false;
  isImageFile: boolean = false;
  isTextFile: boolean = false;
  isHtmlFile: boolean = false;
  textContent: string = '';
  textFallbackUrl: string | null = null;
  fileName: string = 'SheetJS.xlsx';
  isLoading = false;
  isBlobData = false;
  /** Filename of the original Blob, captured before processData() overwrites `data` with a sanitized blob: URL. */
  private blobFileName: string = 'file';
  workbook: XLSX.WorkBook | null = null;
  sheets: SheetView[] = [];
  selectedSheetIndex = 0;
  showFormulas = false;
  showRaw = false;
  useIframe: boolean = false; // Flag to use iframe in template
  originalFileUrl: string | null = null;
  iframePreviewUrl: SafeResourceUrl | null = null;
  // PDFs render via <pdf-viewer> (canvas) by default; these drive the <iframe> fallback on load error.
  pdfFallbackUrl: string | null = null;
  private pdfViewerFailed: boolean = false;
  private initialized = false;
  private blobUrlToRevoke: string | null = null;

  // --- Document Coordinates overlay (FE-3) ---
  @ViewChild('imageHost') imageHost?: ElementRef<HTMLElement>;
  @ViewChild('previewImg') previewImg?: ElementRef<HTMLImageElement>;
  @ViewChild('pdfHost') pdfHost?: ElementRef<HTMLElement>;
  overlayLeft = 0;
  overlayTop = 0;
  overlayWidth = 0;
  overlayHeight = 0;
  /** Sticky "current" page for the <pdf-viewer> navigation (see targetPage). */
  private _docPage = 1;

  // --- Zoom (PDF canvas + image) ---
  /** Zoom multiplier applied on top of the page-fit baseline (1 = fit). */
  zoom = 1;
  private readonly ZOOM_MIN = 0.5;
  private readonly ZOOM_MAX = 3;
  private readonly ZOOM_STEP = 0.25;
  /** Per-page hotspot overlays for the PDF path (doc→list reverse hover, all pages). */
  pdfPageHotspots: PdfPageHotspots[] = [];
  private recomputeScheduled = false;
  private readonly onScrollOrResize = () => this.scheduleRecompute();
  /** Frame/timers for scrolling a new list→doc highlight into view (see scheduleHighlightScrollIntoView). */
  private highlightScrollRaf = 0;
  private highlightScrollTimers: any[] = [];
  /** Geometry signature of the last highlight we scheduled a scroll for (dedupe — see the method). */
  private highlightScrollSig: string | null = null;

  // ── Spreadsheet cell highlight (WB-7592) ──
  @ViewChild('excelBody') excelBody?: ElementRef<HTMLTableSectionElement>;
  /**
   * Resolved highlight as GRID indices. Plain integers, not a `Map<address, variant>`: the template
   * evaluates the cell bindings for every rendered `<td>`, which on a large sheet is tens of
   * thousands of lookups per change-detection cycle. Only one row is ever highlighted.
   */
  private hlRow: number | null = null;
  private hlColStart = 0;
  private hlColEnd = -1;
  /** Field tier (the hovered cell), drawn over the row tier. Empty span when none. */
  private hlFieldColStart = 0;
  private hlFieldColEnd = -1;
  /** Pinned mode (WB-7483). On the table, so it costs no per-cell binding. */
  hlLocked = false;
  private cellHighlightSig: string | null = null;
  /** Last key emitted from a sheet-row hover, so moving within one row doesn't re-emit. */
  private hoveredCellKey: string | null = null;

  constructor(private sanitizer: DomSanitizer, private zone: NgZone) { }

  ngOnInit(): void {
    if (!this.initialized) {
      this.processData();
      this.initialized = true;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && changes['data'].currentValue) {
      this.initialized = true;
      this.processData();
    }
    if (changes['highlight'] || changes['lineRegions']) {
      // Latch the highlighted page immediately so a fast mouse-out (within ng2-pdf-viewer's 100ms
      // pageChange debounce) keeps targetPage sticky instead of reverting to page 1.
      const p = this.highlight?.page;
      if (p) {
        this._docPage = p;
      }
      this.scheduleRecompute();
    }
    if (changes['highlight']) {
      if (this.highlight?.boxes?.length) {
        this.scheduleHighlightScrollIntoView();
      } else {
        this.highlightScrollSig = null; // cleared → re-hovering the same line later scrolls again
      }
    }
    // Paints DOM cells, so no overlay recompute — only a re-resolve.
    if (changes['cellHighlight']) {
      this.applyCellHighlight();
    }
  }

  ngAfterViewInit(): void {
    // capture=true so scrolls inside the pdf-viewer / image container also trigger a recompute
    this.zone.runOutsideAngular(() => {
      window.addEventListener('scroll', this.onScrollOrResize, true);
      window.addEventListener('resize', this.onScrollOrResize);
    });
  }

  get activeBoxes(): HighlightBox[] {
    return this.highlight?.boxes ?? [];
  }

  get highlightPage(): number {
    return this.highlight?.page ?? 1;
  }

  /**
   * The page <pdf-viewer> should display. Driven by the active highlight's page while a line is hovered,
   * otherwise STICKY on the last shown page — so moving the mouse off a line (or sliding between two lines
   * on the same non-first page) never bounces the viewer back to page 1. That transient reset is what
   * triggered ng2-pdf-viewer's debounced `_latestScrolledPage` guard to skip the next scroll (WB-4974).
   */
  get targetPage(): number {
    return this.highlight?.page ?? this._docPage;
  }

  /** Last page actually shown — updated by a highlight's page and by manual scrolling (pageChange). */
  onPdfPageChange(page: number): void {
    if (page) {
      this._docPage = page;
    }
  }

  /** Zoom controls apply only to the canvas PDF / image preview (not iframe, spreadsheet, text…). */
  get canZoom(): boolean {
    return !this.isLoading && !this.useIframe && (this.isPdfFile || this.isImageFile);
  }
  get zoomPercent(): number {
    return Math.round(this.zoom * 100);
  }
  get canZoomIn(): boolean {
    return this.zoom < this.ZOOM_MAX;
  }
  get canZoomOut(): boolean {
    return this.zoom > this.ZOOM_MIN;
  }

  zoomIn(): void {
    this.setZoom(this.zoom + this.ZOOM_STEP);
  }
  zoomOut(): void {
    this.setZoom(this.zoom - this.ZOOM_STEP);
  }
  resetZoom(): void {
    this.setZoom(1);
  }

  private setZoom(value: number): void {
    // Round to 2dp to avoid float drift, then clamp to [MIN, MAX].
    const clamped = Math.min(this.ZOOM_MAX, Math.max(this.ZOOM_MIN, Math.round(value * 100) / 100));
    if (clamped === this.zoom) {
      return;
    }
    this.zoom = clamped;
    // PDF re-renders on the zoom input change → (page-rendered) recomputes the overlay; the image path
    // (CSS transform) fires no event, so recompute explicitly to keep the highlight/hotspots aligned.
    this.scheduleRecompute();
  }

  /** Line regions on the currently rendered page (1-indexed; image path treats everything as page 1). */
  get activeRegions(): DocumentLineRegion[] {
    return (this.lineRegions || []).filter((r) => (r.page ?? 1) === this.highlightPage);
  }

  onHotspotHover(key: string | null): void {
    this.lineHover.emit(key);
  }

  onImageLoad(): void {
    this.scheduleRecompute();
  }

  onPdfPageEvent(): void {
    this.scheduleRecompute();
  }

  private scheduleRecompute(): void {
    if (this.recomputeScheduled) {
      return;
    }
    this.recomputeScheduled = true;
    requestAnimationFrame(() => {
      this.recomputeScheduled = false;
      this.zone.run(() => this.recomputeOverlay());
    });
  }

  /** Rect of `target` relative to the positioned `host`. */
  private relativeRect(host: HTMLElement, target: HTMLElement): { left: number; top: number; width: number; height: number } {
    const hostRect = host.getBoundingClientRect();
    const t = target.getBoundingClientRect();
    return { left: t.left - hostRect.left, top: t.top - hostRect.top, width: t.width, height: t.height };
  }

  trackByPage(_index: number, ph: PdfPageHotspots): number {
    return ph.page;
  }

  /**
   * Measure the overlay rect(s) to cover the rendered document:
   *  - the single highlight overlay (list→doc) over `highlightPage`;
   *  - one hotspot overlay PER page that carries regions (doc→list), so reverse hover works on
   *    every page of a multi-page PDF — not just the active one.
   */
  private recomputeOverlay(): void {
    this.overlayWidth = 0;
    this.overlayHeight = 0;
    this.pdfPageHotspots = [];
    if (!this.activeBoxes.length && !(this.lineRegions?.length)) {
      return;
    }

    if (this.isImageFile && this.previewImg && this.imageHost) {
      // Images are single-page: one rect covers both the highlight and all regions.
      const rect = this.relativeRect(this.imageHost.nativeElement, this.previewImg.nativeElement);
      this.overlayLeft = rect.left;
      this.overlayTop = rect.top;
      this.overlayWidth = rect.width;
      this.overlayHeight = rect.height;
      return;
    }

    if (this.isPdfFile && this.pdfHost) {
      const host = this.pdfHost.nativeElement;

      // Highlight overlay: the page the hovered location lives on.
      const highlightEl = host.querySelector(`.page[data-page-number="${this.highlightPage}"]`) as HTMLElement | null;
      if (highlightEl) {
        const rect = this.relativeRect(host, highlightEl);
        this.overlayLeft = rect.left;
        this.overlayTop = rect.top;
        this.overlayWidth = rect.width;
        this.overlayHeight = rect.height;
      }

      // Hotspot overlays: one per page that has regions and is currently rendered.
      const pages = Array.from(new Set((this.lineRegions || []).map((r) => r.page ?? 1)));
      const overlays: PdfPageHotspots[] = [];
      pages.forEach((page) => {
        const pageEl = host.querySelector(`.page[data-page-number="${page}"]`) as HTMLElement | null;
        if (!pageEl) {
          return;
        }
        const regions = (this.lineRegions || []).filter((r) => (r.page ?? 1) === page);
        if (!regions.length) {
          return;
        }
        const rect = this.relativeRect(host, pageEl);
        overlays.push({ page, regions, left: rect.left, top: rect.top, width: rect.width, height: rect.height });
      });
      this.pdfPageHotspots = overlays;
    }
  }

  /**
   * Document Coordinates (list→doc): after a highlight is set, scroll its box into the previewer's view.
   * `[page]="targetPage"` only scrolls ng2-pdf-viewer at PAGE granularity (landing at the page TOP), so a
   * box lower on the already-shown page — when that page is taller than the container — was drawn correctly
   * but stayed below the fold and never revealed. We fire one immediate frame (snappy for a same-page box)
   * plus two short delayed passes that land AFTER ng2-pdf-viewer's own page scroll settles (~100ms debounce)
   * for the cross-page case. Each pass is idempotent: it only scrolls when the box is actually out of view.
   */
  private scheduleHighlightScrollIntoView(): void {
    if (!this.isPdfFile) {
      return;
    }
    const boxes = this.highlight?.boxes ?? [];
    // `previewHighlight` (the host input) is a getter → a NEW object every change-detection cycle, so
    // ngOnChanges fires on every tick. Dedupe by geometry so a steady hover schedules exactly ONCE —
    // otherwise each tick's clearHighlightScrollTimers() cancels the pending passes before they run.
    const sig = `${this.highlightPage}|` + boxes.map((b) => `${b.x},${b.y},${b.w},${b.h}`).join(';');
    if (sig === this.highlightScrollSig) {
      return;
    }
    this.highlightScrollSig = sig;
    this.clearHighlightScrollTimers();
    this.zone.runOutsideAngular(() => {
      this.highlightScrollRaf = requestAnimationFrame(() => this.tryScrollHighlightIntoView());
      this.highlightScrollTimers.push(
        setTimeout(() => this.tryScrollHighlightIntoView(), 180),
        setTimeout(() => this.tryScrollHighlightIntoView(), 380)
      );
    });
  }

  private clearHighlightScrollTimers(): void {
    if (this.highlightScrollRaf) {
      cancelAnimationFrame(this.highlightScrollRaf);
      this.highlightScrollRaf = 0;
    }
    this.highlightScrollTimers.forEach((t) => clearTimeout(t));
    this.highlightScrollTimers = [];
  }

  /** Scroll the current highlight's box into view (only if it isn't already), across any scroll container. */
  private tryScrollHighlightIntoView(): void {
    const boxes = this.highlight?.boxes;
    if (!this.isPdfFile || !this.pdfHost || !boxes?.length) {
      return;
    }
    const pageEl = this.pdfHost.nativeElement
      .querySelector(`.page[data-page-number="${this.highlightPage}"]`) as HTMLElement | null;
    const h = pageEl?.clientHeight ?? 0;
    if (!pageEl || !h) {
      return; // page not rendered yet — a later pass will catch it
    }
    const topN = Math.min(...boxes.map((b) => b.y));
    const botN = Math.max(...boxes.map((b) => b.y + b.h));
    // Drop a transient marker at the box position inside the (position:relative) page and let the browser
    // scroll it into view. This walks the real — possibly nested — scroll container(s) with NO overflow/
    // class guesswork (the ng2 container only declares overflow-x), and only scrolls when the box is
    // actually off-screen. ~16px breathing room. The marker is added and removed synchronously.
    const pad = 16;
    const marker = document.createElement('div');
    marker.style.cssText =
      'position:absolute;left:0;width:1px;pointer-events:none;visibility:hidden;' +
      `top:${Math.max(0, topN * h - pad)}px;height:${(botN - topN) * h + pad * 2}px;`;
    pageEl.appendChild(marker);
    marker.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    pageEl.removeChild(marker);
    this.scheduleRecompute(); // reposition the overlay after any scroll
  }

  // ── Spreadsheet cell highlight (WB-7592) ──
  // A spreadsheet preview is a real HTML table, not a canvas, so an absolute A1 range resolves to
  // grid indices and CSS paints the cells. No overlay, so nothing to re-measure.

  /** Needs a rendered grid — not the Office-viewer iframe fallback, not an empty workbook. */
  private get canHighlightCells(): boolean {
    return this.isExcelOrCsvFile && !this.useIframe && this.sheets.length > 0;
  }

  /** Row tier — the whole highlighted line. */
  isHlCell(rowIndex: number, colIndex: number): boolean {
    return rowIndex === this.hlRow && colIndex >= this.hlColStart && colIndex <= this.hlColEnd;
  }

  /** Field tier — the hovered cell, drawn over the row tier. */
  isHlField(rowIndex: number, colIndex: number): boolean {
    return rowIndex === this.hlRow && colIndex >= this.hlFieldColStart && colIndex <= this.hlFieldColEnd;
  }

  /**
   * Resolve `cellHighlight` into grid indices, switch sheet and scroll the row into view.
   *
   * Signature-gated: the host binds a GETTER, so `cellHighlight` is a fresh object every CD cycle and
   * `ngOnChanges` fires on every tick. Clearing the signature when the highlight goes away is what
   * lets the same line resolve again later. `force` re-applies after `parseWorkbook`, for a highlight
   * that arrived before the sheets existed.
   */
  private applyCellHighlight(force = false): void {
    const hl = this.cellHighlight;
    const sig = hl ? `${hl.sheet ?? ''}|` + (hl.ranges || []).map((r) => `${r.range}:${r.variant ?? ''}`).join(';') : null;
    if (!force && sig === this.cellHighlightSig) {
      return;
    }
    this.cellHighlightSig = sig;
    this.clearCellHighlight();
    if (!hl || !this.canHighlightCells) {
      return;
    }
    const sheetIndex = this.resolveSheetIndex(hl.sheet);
    if (sheetIndex === -1) {
      return; // names a sheet this workbook hasn't got — paint nothing rather than the wrong sheet
    }
    const sheet = this.sheets[sheetIndex];
    const tiers: Array<{ span: SheetRowSpan; variant?: HighlightVariant }> = [];
    (hl.ranges || []).forEach((r) => {
      const span = decodeRowSpan(r.range);
      if (span) {
        tiers.push({ span, variant: r.variant });
      }
    });
    if (!tiers.length) {
      return;
    }
    // The first (weakest) tier owns the row; later tiers only draw on top of it.
    const rowTier = tiers[0];
    const gridRow = rowTier.span.row - sheet.startR;
    if (gridRow < 0 || gridRow >= sheet.grid.length) {
      return; // outside the rendered used range
    }
    this.hlRow = gridRow;
    this.hlColStart = Math.max(0, rowTier.span.colStart - sheet.startC);
    this.hlColEnd = Math.min(sheet.cols - 1, rowTier.span.colEnd - sheet.startC);
    this.hlLocked = tiers.some((t) => t.variant === 'locked');

    const field = tiers.slice(1).find((t) => t.variant === 'field' && t.span.row === rowTier.span.row);
    if (field) {
      this.hlFieldColStart = Math.max(0, field.span.colStart - sheet.startC);
      this.hlFieldColEnd = Math.min(sheet.cols - 1, field.span.colEnd - sheet.startC);
    }

    // Assigned, not a getter, so the sheet is STICKY — no revert on hover-out, and a manual tab click
    // holds until the next different highlight. Same intent as `targetPage` on the PDF path.
    this.selectedSheetIndex = sheetIndex;
    this.scheduleCellScrollIntoView();
  }

  private clearCellHighlight(): void {
    this.hlRow = null;
    this.hlColStart = 0;
    this.hlColEnd = -1;
    this.hlFieldColStart = 0;
    this.hlFieldColEnd = -1;
    this.hlLocked = false;
  }

  /**
   * Index of the sheet a location names, or -1 when the workbook hasn't got it. A single-sheet
   * workbook IGNORES the name: SheetJS invents one for a CSV, so matching it would drop every CSV
   * highlight (settled on WB-7592). Exact match first, then trimmed/case-insensitive.
   */
  private resolveSheetIndex(sheet?: string): number {
    if (this.sheets.length <= 1) {
      return this.sheets.length ? 0 : -1;
    }
    if (!sheet) {
      return this.selectedSheetIndex; // multi-sheet but unnamed — stay on the sheet already shown
    }
    const exact = this.sheets.findIndex((s) => s.name === sheet);
    if (exact !== -1) {
      return exact;
    }
    const wanted = sheet.trim().toLowerCase();
    return this.sheets.findIndex((s) => (s.name || '').trim().toLowerCase() === wanted);
  }

  /** One frame for the same-sheet case, plus a delayed pass for after a sheet switch rebuilds the grid. */
  private scheduleCellScrollIntoView(): void {
    this.clearHighlightScrollTimers();
    this.zone.runOutsideAngular(() => {
      this.highlightScrollRaf = requestAnimationFrame(() => this.tryScrollCellIntoView());
      this.highlightScrollTimers.push(setTimeout(() => this.tryScrollCellIntoView(), 180));
    });
  }

  private tryScrollCellIntoView(): void {
    if (this.hlRow === null) {
      return;
    }
    const row = this.excelBody?.nativeElement?.rows?.[this.hlRow];
    if (!row) {
      return; // grid not rendered yet — the delayed pass catches it
    }
    // The first highlighted CELL, so a wide sheet scrolls horizontally to the range too.
    const target: HTMLElement = row.cells?.[this.hlColStart] ?? row;
    target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  /**
   * Reverse hover: a sheet row was hovered → emit the matching list line's key.
   * Delegated on the `<tbody>`, not bound per `<tr>` — `mouseover` bubbles where `mouseenter`
   * doesn't, so this is two listeners instead of two per row on a thousand-row sheet.
   */
  onSheetRowHover(event: Event): void {
    if (!this.cellRegions?.length || !this.canHighlightCells) {
      return;
    }
    const row = (event.target as HTMLElement | null)?.closest('tr') as HTMLTableRowElement | null;
    const gridRow = row?.sectionRowIndex ?? -1;
    if (gridRow < 0) {
      return;
    }
    const sheet = this.sheets[this.selectedSheetIndex];
    this.emitCellRowKey(this.lineKeyForSheetRow(sheet, gridRow + (sheet?.startR ?? 0) + 1));
  }

  onSheetRowHoverEnd(): void {
    this.emitCellRowKey(null);
  }

  /** Deduped: `mouseover` re-fires on every cell of the same row, and each emit costs the host a CD pass. */
  private emitCellRowKey(key: string | null): void {
    if (key === this.hoveredCellKey) {
      return;
    }
    this.hoveredCellKey = key;
    this.lineHover.emit(key);
  }

  /** The line at this ABSOLUTE 1-indexed sheet row. Scanned per hover — an index would need
   *  invalidating on both a new payload and every sheet-tab change. */
  private lineKeyForSheetRow(sheet: SheetView | undefined, absRow: number): string | null {
    const singleSheet = this.sheets.length <= 1;
    const match = (this.cellRegions || []).find(
      (r) => r.row === absRow && (singleSheet || !r.sheet || r.sheet === sheet?.name)
    );
    return match?.key ?? null;
  }

  private isBlob(item: any): boolean {
    return item instanceof Blob;
  }

  private isUrl(item: any): boolean {
    return typeof item === 'string' && (item.startsWith('http://') || item.startsWith('https://') || item.startsWith('blob:'));
  }

  private isRelativeUrl(item: any): boolean {
    return typeof item === 'string' && !item.startsWith('http://') && !item.startsWith('https://') && !item.startsWith('blob:');
  }

  private isImageFormat(fileName: string): boolean {
    const imageExtensions = ['.png', '.jpg', '.jpeg', '.gif', '.bmp', '.webp', '.svg', '.tiff', '.tif', '.ico'];
    const lowerFileName = fileName.toLowerCase().split('?')[0];
    return imageExtensions.some(ext => lowerFileName.endsWith(ext));
  }

  private isTextFormat(fileName: string): boolean {
    const textExtensions = ['.txt', '.log', '.md', '.json', '.xml'];
    const lowerFileName = fileName.toLowerCase().split('?')[0];
    return textExtensions.some(ext => lowerFileName.endsWith(ext));
  }

  private isHtmlFormat(fileName: string): boolean {
    const htmlExtensions = ['.html', '.htm'];
    const lowerFileName = fileName.toLowerCase().split('?')[0];
    return htmlExtensions.some(ext => lowerFileName.endsWith(ext));
  }

  getUrlString(url: any): string {
    return this.isUrl(url) ? url : (url as any).changingThisBreaksApplicationSecurity;
  }

  private getFileNameFromBlob(blob: Blob): string {
    if ((blob as any).name) return (blob as any).name;
    if (blob.type) {
      return `file${this.getExtensionFromMimeType(blob.type)}`;
    }
    return 'file';
  }

  private getExtensionFromMimeType(mimeType: string): string {
    const map: { [k: string]: string } = {
      'application/pdf': '.pdf',
      'text/csv': '.csv',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
      'application/vnd.ms-excel': '.xls',
      'application/vnd.ms-excel.sheet.binary.macroEnabled.12': '.xlsb',
      'application/vnd.ms-excel.sheet.macroEnabled.12': '.xlsm',
      'image/png': '.png',
      'image/jpeg': '.jpg',
      'image/jpg': '.jpg',
      'image/gif': '.gif',
      'image/bmp': '.bmp',
      'image/webp': '.webp',
      'image/svg+xml': '.svg',
      'image/tiff': '.tiff',
      'image/x-icon': '.ico',
      'text/plain': '.txt',
      'text/markdown': '.md',
      'application/json': '.json',
      'application/xml': '.xml',
      'text/xml': '.xml',
      'text/html': '.html'
    };
    return map[mimeType] || '';
  }

  private cleanupBlobUrls(): void {
    // Clean up any existing blob URLs before processing new data
    if (this.data.length > 0) {
      this.data.forEach((url: any) => {
        if (url) {
          const urlString = typeof url === 'string' ? url : (url as any).changingThisBreaksApplicationSecurity;
          if (urlString && urlString.startsWith('blob:')) {
            URL.revokeObjectURL(urlString);
          }
        }
      });
    }
  }

  private resetState() {
    // Clean up blob URLs before resetting state
    this.cleanupBlobUrls();
    if (this.blobUrlToRevoke) {
      URL.revokeObjectURL(this.blobUrlToRevoke);
      this.blobUrlToRevoke = null;
    }
    this.isExcelOrCsvFile = false;
    this.isPdfFile = false;
    this.isImageFile = false;
    this.isTextFile = false;
    this.isHtmlFile = false;
    this.textContent = '';
    this.textFallbackUrl = null;
    this.isLoading = false;
    this.isBlobData = false;
    this.blobFileName = 'file';
    this.workbook = null;
    this.sheets = [];
    this.selectedSheetIndex = 0;
    this.useIframe = false;
    this.originalFileUrl = null;
    this.iframePreviewUrl = null;
    this.pdfFallbackUrl = null;
    this.pdfViewerFailed = false;
    this.zoom = 1;
    // Signature too: the same range means a different row once another workbook's origin is in play.
    this.clearCellHighlight();
    this.cellHighlightSig = null;
    this.hoveredCellKey = null;
  }

  private stripQuery(url: string): string {
    return (url || '').split('?')[0];
  }

  private isSpreadsheetPath(pathLower: string): boolean {
    return (
      pathLower.endsWith('.csv') ||
      pathLower.endsWith('.xlsx') ||
      pathLower.endsWith('.xls') ||
      pathLower.endsWith('.xlsm') ||
      pathLower.endsWith('.xlsb')
    );
  }

  private isCrossOriginHttpUrl(url: string): boolean {
    try {
      const parsed = new URL(url, window.location.href);
      return (parsed.protocol === 'http:' || parsed.protocol === 'https:') && parsed.origin !== window.location.origin;
    } catch {
      return false;
    }
  }

  private enableOfficeViewerIframe(originalUrl: string): void {
    // Office viewer fetches the file server-side, so it bypasses browser CORS restrictions.
    // This requires the URL to be reachable by the Office viewer service.
    const viewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(originalUrl)}`;
    this.originalFileUrl = originalUrl;
    this.iframePreviewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(viewerUrl);
    this.data = [this.iframePreviewUrl];
    this.useIframe = true;
    this.workbook = null;
    this.sheets = [];
    this.isLoading = false;
    this.isExcelOrCsvFile = true;
  }

  private async resolveRelativeUrls(items: any[]): Promise<Blob[]> {
    return Promise.all(
      items.map(async (item) => {
        const fullUrl = environment.BASE_URL_API + item;
        const response = await fetch(fullUrl, { headers: this.apiAuthHeaders(fullUrl) });

        if (!response.ok) {
          throw new Error(`Failed to fetch ${fullUrl}: ${response.status} ${response.statusText}`);
        }

        const contentType = response.headers.get('Content-Type') || 'application/octet-stream';
        const arrayBuffer = await response.arrayBuffer();
        return new Blob([arrayBuffer], { type: contentType });
      })
    );
  }

  private processData(): void {
    if (!this.data || this.data.length === 0) {
      this.resetState();
      return;
    }
    this.resetState();
    this.isLoading = true;

    const hasRelativeUrls = this.data.some((item: any) => this.isRelativeUrl(item));
    if (hasRelativeUrls) {
      const relativeItems = this.data.filter((item: any) => this.isRelativeUrl(item));
      const fullUrl = environment.BASE_URL_API + relativeItems[0];
      const headers = this.apiAuthHeaders(fullUrl);

      fetch(fullUrl, { method: 'HEAD', headers })
        .then(response => {
          const contentType = response.headers.get('Content-Type') || '';

          if (contentType.includes('application/pdf')) {
            // PDF: fetch with auth and render from a blob URL (native loads can't carry the token).
            this.loadApiUrlAsBlob(fullUrl);
          } else if (contentType.includes('text/html')) {
            // HTML: render in a sandboxed iframe so scripts/forms are blocked
            this.data = [this.sanitizer.bypassSecurityTrustResourceUrl(fullUrl)];
            this.isHtmlFile = true;
            this.isLoading = false;
          } else if (contentType.startsWith('text/') || contentType.includes('application/json') || contentType.includes('application/xml')) {
            // Text: fetch content directly, no Blob conversion needed
            fetch(fullUrl, { headers })
              .then(r => {
                if (!r.ok) throw new Error(`Fetch failed: ${r.status} ${r.statusText}`);
                return r.text();
              })
              .then(text => {
                this.textContent = text;
                this.isTextFile = true;
                this.isLoading = false;
              })
              .catch(err => {
                console.error('Error loading text file:', err);
                this.isLoading = false;
              });
          } else {
            // Other types: fetch fully and convert to typed Blob
            this.resolveRelativeUrls(relativeItems)
              .then(blobs => {
                this.data = blobs;
                this.processData();
              })
              .catch(err => {
                console.error('Error resolving relative URLs:', err);
                this.isLoading = false;
              });
          }
        })
        .catch(err => {
          console.error('Error checking content type:', err);
          this.isLoading = false;
        });
      return;
    }

    this.isBlobData = this.data.some((item: any) => this.isBlob(item));
    if (this.isBlobData) {
      // convert Blob -> arrayBuffer -> parse
      const first = this.data[0] as Blob;
      this.blobFileName = this.getFileNameFromBlob(first);
      const filename = this.blobFileName.toLowerCase();
      this.isPdfFile = filename.endsWith('.pdf');
      this.isExcelOrCsvFile = filename.endsWith('.csv') || filename.endsWith('.xlsx') || filename.endsWith('.xls') || filename.endsWith('.xlsm') || filename.endsWith('.xlsb');
      this.isImageFile = this.isImageFormat(filename);
      this.isTextFile = this.isTextFormat(filename);
      this.isHtmlFile = this.isHtmlFormat(filename);

      if (this.isExcelOrCsvFile) {
        const fr = new FileReader();
        fr.onload = (e: any) => {
          const ab = e.target.result as ArrayBuffer;
          this.parseWorkbook(ab);
        };
        fr.onerror = (err) => { console.error(err); this.isLoading = false; };
        fr.readAsArrayBuffer(first);
      } else if (this.isHtmlFile) {
        const htmlBlob = first.type ? first : new Blob([first], { type: 'text/html' });
        const blobUrl = URL.createObjectURL(htmlBlob);
        this.blobUrlToRevoke = blobUrl;
        this.data = [this.sanitizer.bypassSecurityTrustResourceUrl(blobUrl)];
        this.isLoading = false;
      } else if (this.isTextFile) {
        const fr = new FileReader();
        fr.onload = (e: any) => {
          this.textContent = (e.target.result as string) || '';
          this.isLoading = false;
        };
        fr.onerror = (err) => { console.error(err); this.isLoading = false; };
        fr.readAsText(first);
      } else {
        this.data = this.data.map((blob: Blob) => {
          const url = URL.createObjectURL(blob);
          return this.sanitizer.bypassSecurityTrustUrl(url);
        });
        this.isLoading = false;
      }
    } else {
      // URL case
      const firstUrl = typeof this.data[0] === 'string' ? this.data[0] : (this.data[0] as any).changingThisBreaksApplicationSecurity;
      if (typeof firstUrl === 'string' && firstUrl.startsWith(environment.BASE_URL)) {
        // Backend attachment (e.g. the proxy route). Native <pdf-viewer>/<img>/<iframe> loads can't send
        // the Authorization header, so fetch the bytes WITH the token and render from a blob URL.
        this.loadApiUrlAsBlob(firstUrl);
        return;
      }
      const lowerPath = this.stripQuery(firstUrl).toLowerCase();
      this.isPdfFile = lowerPath.endsWith('.pdf');
      this.isExcelOrCsvFile = this.isSpreadsheetPath(lowerPath);
      this.isImageFile = this.isImageFormat(firstUrl);
      this.isTextFile = this.isTextFormat(firstUrl);
      this.isHtmlFile = this.isHtmlFormat(firstUrl);

      if (this.isHtmlFile) {
        this.data = [this.sanitizer.bypassSecurityTrustResourceUrl(firstUrl)];
        this.isLoading = false;
      } else if (this.isTextFile) {
        fetch(firstUrl)
          .then(r => {
            if (!r.ok) throw new Error(`Fetch failed: ${r.status} ${r.statusText}`);
            return r.text();
          })
          .then(text => {
            this.textContent = text;
            this.isLoading = false;
          })
          .catch(err => {
            console.error('Error loading text file:', err);
            if (this.isCrossOriginHttpUrl(firstUrl)) {
              this.textFallbackUrl = firstUrl;
            }
            this.isLoading = false;
          });
      } else if (this.isExcelOrCsvFile) {
        fetch(firstUrl)
          .then(r => r.arrayBuffer())
          .then(ab => this.parseWorkbook(ab))
          .catch(err => {
            console.error(err);
            // If CORS blocks the fetch (common for signed GCS URLs), fall back to an iframe Office viewer.
            // Only do this for cross-origin http(s) URLs (same-origin and authenticated URLs won't work in Office viewer).
            if (this.isCrossOriginHttpUrl(firstUrl)) {
              this.enableOfficeViewerIframe(firstUrl);
              return;
            }
            this.isLoading = false;
          });
      } else if (this.isPdfFile) {
        // Default to <pdf-viewer> (canvas) so coordinate overlays are possible. pdf.js fetches/sniffs the
        // bytes itself, so we don't pre-check the content-type. If it can't load (e.g. a cross-origin URL
        // without CORS) the (error) output calls onPdfViewerError(), which falls back to an <iframe>.
        this.pdfFallbackUrl = firstUrl;
        this.pdfViewerFailed = false;
        this.useIframe = false;
        this.isLoading = false;
      } else if (firstUrl.startsWith('http://') || firstUrl.startsWith('https://')) {
        // Extension-less URL (e.g. the BE attachment proxy route) — classify by Content-Type.
        this.classifyByContentType(firstUrl);
      } else {
        this.isLoading = false;
      }
    }
  }

  /** Probe an extension-less URL's Content-Type (HEAD) and render accordingly. */
  private classifyByContentType(url: string): void {
    fetch(url, { method: 'HEAD' })
      .then((resp) => {
        const contentType = (resp.headers.get('Content-Type') || '').toLowerCase();
        if (!resp.ok || !contentType) {
          // HEAD not supported / errored — assume PDF (the common attachment) with iframe fallback.
          this.renderAsPdf(url);
          return;
        }
        this.applyContentType(url, contentType);
      })
      .catch(() => {
        // CORS/network: can't read the type. Attachments are usually PDFs — let <pdf-viewer> try and
        // fall back to <iframe> on (error).
        this.renderAsPdf(url);
      });
  }

  private applyContentType(url: string, contentType: string): void {
    if (contentType.includes('application/pdf')) {
      this.renderAsPdf(url);
    } else if (contentType.startsWith('image/')) {
      this.isImageFile = true;
      this.isLoading = false;
    } else if (contentType.includes('text/html')) {
      this.isHtmlFile = true;
      this.data = [this.sanitizer.bypassSecurityTrustResourceUrl(url)];
      this.isLoading = false;
    } else if (contentType.startsWith('text/') || contentType.includes('application/json') || contentType.includes('application/xml')) {
      fetch(url)
        .then((r) => r.text())
        .then((text) => {
          this.textContent = text;
          this.isTextFile = true;
          this.isLoading = false;
        })
        .catch(() => {
          if (this.isCrossOriginHttpUrl(url)) {
            this.textFallbackUrl = url;
            this.isTextFile = true;
          }
          this.isLoading = false;
        });
    } else if (contentType.includes('spreadsheet') || contentType.includes('ms-excel') || contentType.includes('csv')) {
      fetch(url)
        .then((r) => r.arrayBuffer())
        .then((ab) => this.parseWorkbook(ab))
        .catch(() => {
          if (this.isCrossOriginHttpUrl(url)) {
            this.enableOfficeViewerIframe(url);
            return;
          }
          this.isLoading = false;
        });
    } else {
      // Unknown type — attachments are most often PDFs; try the viewer with iframe fallback.
      this.renderAsPdf(url);
    }
  }

  private renderAsPdf(url: string): void {
    this.isPdfFile = true;
    this.pdfFallbackUrl = url;
    this.pdfViewerFailed = false;
    this.useIframe = false;
    this.isLoading = false;
  }

  private getAccessToken(): string {
    const currentUser = LocalStorageService.get('currentUser');
    return currentUser?.access_token || '';
  }

  /** Auth headers for our backend URLs (mirrors TokenInterceptor); empty for third-party URLs. */
  private apiAuthHeaders(url: string): HeadersInit {
    const token = this.getAccessToken();
    if (token && url.startsWith(environment.BASE_URL)) {
      return { Authorization: `Bearer ${token}` };
    }
    return {};
  }

  /**
   * Fetch a backend attachment WITH the auth token and render it from a blob URL. Native element loads
   * (<pdf-viewer>, <img>, <iframe>) can't carry an Authorization header, so we download the bytes here;
   * rendering from the resulting blob also avoids CORS and lets the canvas overlay work.
   */
  private loadApiUrlAsBlob(url: string): void {
    fetch(url, { headers: this.apiAuthHeaders(url) })
      .then((resp) => {
        if (!resp.ok) {
          throw new Error(`Fetch failed: ${resp.status} ${resp.statusText}`);
        }
        const headerType = (resp.headers.get('Content-Type') || '').split(';')[0].trim().toLowerCase();
        return resp.arrayBuffer().then((ab) => {
          // Prefer the server Content-Type; fall back to the filename extension when it's missing/generic.
          const type = headerType && headerType !== 'application/octet-stream'
            ? headerType
            : this.getMimeTypeFromExtension(url);
          // Use a File (not a bare Blob) so the blob branch can classify by the real filename + extension.
          return new File([ab], this.deriveFileName(url, type), { type });
        });
      })
      .then((file) => {
        this.data = [file];
        this.processData();
      })
      .catch((err) => {
        console.error('Error loading attachment:', err);
        // Couldn't download (CORS/auth/network) — fall back to rendering the URL directly
        // (pdf-viewer, then <iframe> on error), which works when the endpoint needs no request header.
        this.renderAsPdf(url);
      });
  }

  /** MIME type from a URL/filename extension (fallback when the server Content-Type is missing/generic). */
  private getMimeTypeFromExtension(url: string): string {
    const ext = this.stripQuery(url).toLowerCase().split('.').pop() || '';
    const map: { [k: string]: string } = {
      pdf: 'application/pdf',
      png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif',
      bmp: 'image/bmp', webp: 'image/webp', svg: 'image/svg+xml', tiff: 'image/tiff', tif: 'image/tiff', ico: 'image/x-icon',
      csv: 'text/csv', xls: 'application/vnd.ms-excel',
      xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      xlsm: 'application/vnd.ms-excel.sheet.macroEnabled.12',
      xlsb: 'application/vnd.ms-excel.sheet.binary.macroEnabled.12',
      txt: 'text/plain', log: 'text/plain', md: 'text/markdown', json: 'application/json', xml: 'application/xml',
      html: 'text/html', htm: 'text/html',
    };
    return map[ext] || 'application/octet-stream';
  }

  /** Last path segment as a filename; appends an extension derived from the MIME type when missing. */
  private deriveFileName(url: string, mimeType: string): string {
    const raw = this.stripQuery(url).split('/').pop() || 'file';
    let name = raw;
    try {
      name = decodeURIComponent(raw);
    } catch {
      name = raw;
    }
    const lower = name.toLowerCase();
    const hasKnownExt =
      lower.endsWith('.pdf') ||
      this.isImageFormat(lower) ||
      this.isSpreadsheetPath(lower) ||
      this.isTextFormat(lower) ||
      this.isHtmlFormat(lower);
    return hasKnownExt ? name : name + this.getExtensionFromMimeType(mimeType);
  }

  private parseWorkbook(arrayBuffer: ArrayBuffer) {
    try {
      const wb = XLSX.read(arrayBuffer, {
        type: 'array',
        cellDates: true,
        cellNF: true,
        cellStyles: true,
        WTF: true
      });
      this.workbook = wb;

      this.sheets = wb.SheetNames.map(name => {
        const ws = wb.Sheets[name];
        // determine range; if none, use A1..A1
        const ref = ws['!ref'] || 'A1';
        const range = XLSX.utils.decode_range(ref);
        const startR = range.s.r;
        const startC = range.s.c;
        const endR = range.e.r;
        const endC = range.e.c;
        const rows = endR - startR + 1;
        const cols = endC - startC + 1;

        // Build blank grid of that size
        const grid: CellView[][] = Array.from({ length: rows }, () =>
          Array.from({ length: cols }, () => ({
            text: '',
            raw: null,
            formula: null,
            address: '',
          } as CellView))
        );

        // Fill grid cells from worksheet
        for (let R = startR; R <= endR; ++R) {
          for (let C = startC; C <= endC; ++C) {
            const addr = XLSX.utils.encode_cell({ r: R, c: C });
            const cell = ws[addr];
            const rr = R - startR;
            const cc = C - startC;
            if (cell) {
              const formatted = (cell.w !== undefined) ? String(cell.w) : (cell.v !== undefined ? String(cell.v) : '');
              grid[rr][cc] = {
                text: formatted,
                raw: cell.v,
                formula: cell.f || null,
                address: addr
              };
            } else {
              grid[rr][cc] = {
                text: '',
                raw: null,
                formula: null,
                address: XLSX.utils.encode_cell({ r: R, c: C })
              };
            }
          }
        }

        // merges -> rowspan / colspan and hide covered cells
        const merges = ws['!merges'] || [];
        merges.forEach((m: any) => {
          const r0 = m.s.r - startR;
          const c0 = m.s.c - startC;
          const rowspan = m.e.r - m.s.r + 1;
          const colspan = m.e.c - m.s.c + 1;
          if (r0 >= 0 && c0 >= 0 && r0 < rows && c0 < cols) {
            grid[r0][c0].rowspan = rowspan;
            grid[r0][c0].colspan = colspan;
            for (let rr = r0; rr < r0 + rowspan; rr++) {
              for (let cc = c0; cc < c0 + colspan; cc++) {
                if (rr === r0 && cc === c0) continue;
                if (rr >= 0 && rr < rows && cc >= 0 && cc < cols) {
                  grid[rr][cc].hidden = true;
                }
              }
            }
          }
        });

        // Compute last non-empty row/col by scanning cell keys (exclude keys starting with '!')
        let maxR = -1;
        let maxC = -1;
        Object.keys(ws).forEach(k => {
          if (k[0] === '!') return; // skip metadata like !ref, !merges
          const decoded = XLSX.utils.decode_cell(k);
          if (decoded.r > maxR) maxR = decoded.r;
          if (decoded.c > maxC) maxC = decoded.c;
        });

        const hidden = (wb.Workbook && wb.Workbook.Sheets && wb.Workbook.Sheets.find(s => s.name === name && s.Hidden)) ? true : false;

        return {
          name,
          grid,
          rows,
          cols,
          visible: true,
          hidden,
          startR,
          startC,
        } as SheetView;
      });

      this.selectedSheetIndex = this.sheets.findIndex(s => !s.hidden);
      if (this.selectedSheetIndex === -1) this.selectedSheetIndex = 0;

      this.isLoading = false;
      this.isExcelOrCsvFile = true;
      // Parsing is async, so a highlight bound before now resolved against no sheets. Forced,
      // because its signature is unchanged and the normal gate would skip it.
      this.applyCellHighlight(true);
    } catch (err) {
      console.error('Error parsing workbook', err);
      this.isLoading = false;
    }
  }

  setSelectedSheet(i: number) {
    this.selectedSheetIndex = i;
  }

  getSelectedSheet(): SheetView | null {
    return (this.sheets && this.sheets.length > 0) ? this.sheets[this.selectedSheetIndex] : null;
  }

  export(): void {
    if (this.workbook) {
      try {
        // save original workbook file (name fallback)
        const fileName = 'exported-workbook.xlsx';
        XLSX.writeFile(this.workbook, fileName);
      } catch (err) {
        console.error('Export error', err);
      }
    }else if(this.isPdfFile || this.isImageFile || this.isTextFile || this.isHtmlFile){
      if (this.isTextFile && this.textContent && !this.isBlobData) {
        const blob = new Blob([this.textContent], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'file.txt';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        return;
      }
      if (this.isBlobData) {
        // Handle blob export. `this.data[0]` is only still the raw Blob for text files — for PDF/image/HTML
        // blobs, processData() already replaced it with a sanitized wrapper around a blob: URL it created
        // (and, for HTML, is tracked in `blobUrlToRevoke`), so reuse that URL instead of re-wrapping a
        // non-Blob value (which throws "Overload resolution failed" on URL.createObjectURL).
        const first = this.data[0];
        const isRawBlob = this.isBlob(first);
        const url = isRawBlob ? URL.createObjectURL(first) : this.getUrlString(first);
        const link = document.createElement('a');
        link.href = url;
        link.download = this.blobFileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        if (isRawBlob) {
          URL.revokeObjectURL(url);
        }
      } else {
        // Handle URL export
        const urlToFetch = typeof this.data[0] === 'string' ? 
          this.data[0] : (this.data[0] as any).changingThisBreaksApplicationSecurity;
        
        fetch(urlToFetch)
          .then(response => response.blob())
          .then(blob => {
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            // Set a default filename based on file type
            let defaultFileName = 'file';
            if (this.isPdfFile) {
              defaultFileName = this.fileName.endsWith('.pdf') ? this.fileName : 'file.pdf';
            } else if (this.isImageFile) {
              const urlParts = urlToFetch.split('/');
              const fileName = urlParts[urlParts.length - 1];
              defaultFileName = fileName || 'image.png';
            }
            link.download = defaultFileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
          })
          .catch(error => {
            console.error('Error downloading file:', error);
          });
      }
    }
	}

  /** <pdf-viewer> failed to load the PDF (commonly cross-origin without CORS) — fall back to an <iframe>. */
  onPdfViewerError(): void {
    if (this.pdfViewerFailed || !this.pdfFallbackUrl) {
      return;
    }
    this.pdfViewerFailed = true;
    const url = this.pdfFallbackUrl;
    const urlWithHiddenNav = url + (url.includes('#') ? '&navpanes=0&view=FitH' : '#navpanes=0&view=FitH');
    this.data = [this.sanitizer.bypassSecurityTrustResourceUrl(urlWithHiddenNav)];
    this.useIframe = true;
  }

  onImageError(event: any): void {
    console.error('Error loading image:', event);
  }

  ngOnDestroy(): void {
    window.removeEventListener('scroll', this.onScrollOrResize, true);
    window.removeEventListener('resize', this.onScrollOrResize);
    this.clearHighlightScrollTimers();
    // Clean up blob URLs to prevent memory leaks
    this.cleanupBlobUrls();
    if (this.blobUrlToRevoke) {
      URL.revokeObjectURL(this.blobUrlToRevoke);
      this.blobUrlToRevoke = null;
    }
  }
}
